/**
 * Marketing landing-page pricing-card SOT (Phase 7 Pass 1 iter 267).
 *
 * Source: `/product/market-and-gtm/pricing.md` § "The 4-tier structure" +
 * `packages/shared/src/pricing/uc-tiers.ts` (numeric SOT). The card shows all
 * FOUR tiers — Starter / Growth / Scale (self-serve) + Enterprise (custom).
 * Both the home `PricingSection` and the full `/pricing` page consume this same
 * SOT, so they can never drift.
 *
 * Every tier carries a canonical anchor (price + included UC + one key
 * differentiator) tested as a regression-style assertion. Changing
 * the price requires updating the test in the same change. The numeric
 * `monthlyUsd` mirrors `UC_TIERS[slug].monthlyPriceUsd` (drift-guarded in
 * `billing-period.test.ts`) and drives the annual −10% computation.
 *
 * Phase 9 Pass 1 (AF-420/421): CTA destinations are composed from the
 * canonical `@/lib/site-url` accessors instead of hardcoded routes.
 *
 * Operator directive 2026-07-16: the site is sales-led — EVERY tier CTA
 * (Starter … Enterprise) now routes to the live sales-contact form (`/contact`, Sales
 * topic) rather than self-serve app sign-up. Prices stay visible on the cards;
 * only the button destination + label ("talk to sales") change. Since
 * 2026-08-05 the rest of the site follows: no payment processor is connected,
 * so no marketing surface advertises a self-serve sign-up any more — the
 * global nav's primary CTA is "talk to sales" too, and `appSignupUrl` keeps
 * only returning-user-adjacent plumbing (the helper exists; nothing links it).
 */
import { salesContactHref } from '@/lib/site-url';

/**
 * Every tier — Starter (the paid entry tier) / Growth / Scale / Enterprise.
 * All four render as pricing cards.
 */
export type TierSlug = 'starter' | 'growth' | 'scale' | 'enterprise';

export interface PricingTier {
  readonly slug: TierSlug;
  readonly name: string;
  /** Display price string (kept as-is to honour the pricing.md spec) — the EN
   *  fallback when no `priceKey` is set. Verbatim across locales (a number). */
  readonly price: string;
  /**
   * Numeric monthly list price in USD — the SOT mirror of
   * `UC_TIERS[slug].monthlyPriceUsd`. Drives the annual −10% monthly-equivalent
   * (`annualMonthlyUsd`). Omitted for Enterprise (custom-quoted, no number).
   */
  readonly monthlyUsd?: number;
  /**
   * i18n key for the price when the price label is descriptive rather than a
   * verbatim figure (Enterprise → localized "Custom", ADR-0149). Resolved in
   * `TierCard` via `t()` (mirrors `periodKey`/`allowanceKey`); numbered tiers keep
   * the verbatim `price` literal and leave this unset.
   */
  readonly priceKey?: string;
  /** Optional period label (e.g. "/mo", "/yr") — the EN fallback. Omitted for
   *  tiers whose price is descriptive (Enterprise → "Custom", no period). */
  readonly period?: string;
  /** i18n key for the period unit (I133) — resolved in `TierCard` so the
   *  unit localizes per locale (the price number itself stays verbatim). */
  readonly periodKey?: string;
  /**
   * The tier's included usage credits (UC) — ADR-0149. Numbered tiers keep the
   * value verbatim (`"30,000 UC"` — number + protocol term). Tiers whose
   * allotment is descriptive (Enterprise → "custom") carry an i18n key here
   * instead, resolved in-component via `t()`.
   */
  readonly allowance: string;
  /** When set, the allowance label is an i18n key to resolve instead of `allowance`. */
  readonly allowanceKey?: string;
  /**
   * i18n keys for the 3 short bullet differentiators per tier. Resolved in
   * `TierCard` via `t()` so the bullets transcreate per locale while the SKU
   * names / numbers / UC figures stay verbatim.
   */
  readonly bulletKeys: readonly string[];
  /**
   * The tier the card set visually leads with (Growth). Emphasis only — it
   * carried a "Most popular" badge until 2026-08-18, which asserted something
   * about what CUSTOMERS had chosen; with no customers that was a false claim
   * rather than a soft one, so the badge went and the emphasis stayed.
   * Recommending a tier is ours to do; reporting demand is not.
   */
  readonly highlighted: boolean;
  /** The CTA shown on the card (label is an i18n key, lowercase per brand-voice). */
  readonly cta: { labelKey: string; href: string };
}

export const PRICING_TIERS: readonly PricingTier[] = [
  {
    slug: 'starter',
    name: 'Starter',
    // Starter is the PAID entry tier (no free tier). $299/mo · 30,000 UC ·
    // 5 seats (verified against UC_TIERS.starter). ADR-0489 removed the
    // automatic trial, so no card promises one.
    price: '$299',
    monthlyUsd: 299,
    period: '/mo',
    periodKey: 'pricing.period.monthly',
    allowance: '30,000 UC',
    bulletKeys: [
      'pricing.tier.starter.bullet.seats',
      'pricing.tier.starter.bullet.llm',
      'pricing.tier.starter.bullet.support',
    ],
    highlighted: false,
    cta: { labelKey: 'pricing.tier.starter.cta', href: salesContactHref() },
  },
  {
    slug: 'growth',
    name: 'Growth',
    price: '$999',
    monthlyUsd: 999,
    period: '/mo',
    periodKey: 'pricing.period.monthly',
    allowance: '110,000 UC',
    // ADR-0149 / ADR-0182 — the $999 bundle must be VISIBLE on the card:
    // Frontier (Copilot Mode) is the headline Growth+ differentiator, plus
    // BYOK, branding removal, MCP server, multilingual. Never matrix-only.
    bulletKeys: [
      'pricing.tier.growth.bullet.frontier',
      'pricing.tier.growth.bullet.seats',
      'pricing.tier.growth.bullet.mcp',
      'pricing.tier.growth.bullet.byok',
      'pricing.tier.growth.bullet.multilingual',
      'pricing.tier.growth.bullet.support',
    ],
    highlighted: true,
    cta: { labelKey: 'pricing.tier.growth.cta', href: salesContactHref() },
  },
  {
    slug: 'scale',
    name: 'Scale',
    price: '$2,999',
    monthlyUsd: 2999,
    period: '/mo',
    periodKey: 'pricing.period.monthly',
    allowance: '360,000 UC',
    bulletKeys: [
      'pricing.tier.scale.bullet.frontier',
      'pricing.tier.scale.bullet.seats',
      'pricing.tier.scale.bullet.byok',
      'pricing.tier.scale.bullet.theming',
    ],
    highlighted: false,
    cta: { labelKey: 'pricing.tier.scale.cta', href: salesContactHref() },
  },
  {
    slug: 'enterprise',
    name: 'Enterprise',
    // ADR-0149: Enterprise is custom-quoted — no public price anchor. The card
    // shows a localized "Custom" (priceKey) with no billing period.
    price: 'Custom',
    priceKey: 'pricing.tier.enterprise.price',
    allowance: 'custom',
    allowanceKey: 'pricing.tier.enterprise.includedUc',
    bulletKeys: [
      'pricing.tier.enterprise.bullet.vpc',
      'pricing.tier.enterprise.bullet.soc2',
      'pricing.tier.enterprise.bullet.mcp',
    ],
    highlighted: false,
    cta: { labelKey: 'pricing.tier.enterprise.cta', href: salesContactHref() },
  },
];

/** The four headline tiers rendered as cards (Starter / Growth / Scale / Enterprise). */
export const CARD_TIERS: readonly PricingTier[] = PRICING_TIERS;

/**
 * i18n key for the "no free tier" explainer shown under the tier grid (there is
 * no free tier; Starter is the paid entry tier, gated by a 14-day, no-card
 * trial). It carries the no-free-tier + trial promise the grid can't express on
 * a single card.
 */
export const NO_FREE_TIER_NOTE_KEY = 'pricing.noFreeTierNote';
